import { useEffect, useMemo, useState } from "react";
import {
  ArrowRight,
  CalendarDays,
  Check,
  CheckCircle2,
  Crown,
  Download,
  FileText,
  Gift,
  Info,
  Loader2,
  Lock,
  LockKeyhole,
  Repeat,
  ShieldCheck,
  Sparkles,
  Star,
  Target,
  TrendingUp,
  Trophy,
  Users,
  WalletCards,
  X,
  Zap,
} from "lucide-react";
import { getAuth } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";

import {
  getInvestimentosPeriodo,
  getResumoPeriodo,
  getUltimasTransacoes,
  ouvirCofrinhos,
} from "../services/financeService";

import "../styles/pages/plus.css";

const beneficios = [
  {
    titulo: "Metas e cofrinhos",
    descricao:
      "Crie objetivos, acompanhe o progresso e veja quanto falta para chegar lá.",
    icon: Target,
    cor: "green",
  },
  {
    titulo: "Controle de dívidas",
    descricao:
      "Organize parcelas, vencimentos e pagamentos para não se perder no mês.",
    icon: WalletCards,
    cor: "blue",
  },
  {
    titulo: "Relatórios PDF",
    descricao:
      "Gere resumos premium com gráficos e visão mensal das suas finanças.",
    icon: FileText,
    cor: "purple",
  },
  {
    titulo: "Exportação Excel",
    descricao:
      "Baixe suas movimentações para analisar, guardar ou enviar quando precisar.",
    icon: Download,
    cor: "cyan",
  },
  {
    titulo: "Recorrências",
    descricao: "Automatize contas fixas, assinaturas e lançamentos repetidos.",
    icon: Repeat,
    cor: "orange",
  },
  {
    titulo: "Backup avançado",
    descricao:
      "Mais segurança para seus dados e tranquilidade para continuar usando.",
    icon: ShieldCheck,
    cor: "red",
  },
];

const comparativo = [
  { recurso: "Dashboard mensal", free: true, plus: true },
  { recurso: "Entradas e saídas", free: true, plus: true },
  { recurso: "Histórico básico", free: true, plus: true },
  { recurso: "Categorias", free: true, plus: true },
  { recurso: "Metas e cofrinhos", free: false, plus: true },
  { recurso: "Controle de dívidas", free: false, plus: true },
  { recurso: "Relatórios premium", free: false, plus: true },
  { recurso: "Exportação Excel", free: false, plus: true },
  { recurso: "Recorrências", free: false, plus: true },
];

const classesApoiador = [
  { id: "explorer", nome: "Explorer", meta: 1, tipo: "leaf" },
  { id: "bronze", nome: "Bronze", meta: 3, tipo: "star" },
  { id: "silver", nome: "Silver", meta: 5, tipo: "star" },
  { id: "gold", nome: "Gold", meta: 10, tipo: "star" },
  { id: "founder", nome: "Founder", meta: 25, tipo: "x" },
];

const selosHistoricos = [
  {
    ano: 2026,
    titulo: "FOUNDER 2026",
    status: "ATIVO",
    estado: "ativo",
    descricao: "Primeira geração de apoiadores do Invexa.",
  },
  {
    ano: 2027,
    titulo: "FOUNDER 2027",
    status: "EM BREVE",
    estado: "misterio-blue",
    descricao: "Uma nova geração surgirá.",
  },
  {
    ano: 2028,
    titulo: "FOUNDER 2028",
    status: "EM BREVE",
    estado: "misterio-purple",
    descricao: "Segredo do próximo ciclo.",
  },
  {
    ano: 2029,
    titulo: "FOUNDER 2029",
    status: "BLOQUEADO",
    estado: "bloqueado",
    descricao: "Ainda não revelado.",
  },
  {
    ano: 2030,
    titulo: "FOUNDER 2030",
    status: "BLOQUEADO",
    estado: "bloqueado",
    descricao: "Ainda não revelado.",
  },
];

// Apoiadores reais: alimente pelo billing.apoiadoresIniciais quando tiver usuários Founder reais.
// Exemplo esperado:
// billing.apoiadoresIniciais = [
//   { nome: "Nome real", selo: "FOUNDER 2026", iniciais: "N" }
// ];
const apoiadoresIniciais = [];

const proximasAtualizacoes = [
  {
    status: "EM BREVE",
    titulo: "Lembretes de Contas",
    descricao: "Visualizações ainda mais completas e interativas.",
    cor: "green",
  },
  {
    status: "EM BREVE",
    titulo: "Foto de Perfil",
    descricao: "Visualizações ainda mais completas e interativas.",
    cor: "green",
  },
 
];

function plusAtivo(billing) {
  if (!billing) return false;

  const agora = Date.now();

  const accessExpiresAt =
    billing?.accessExpiresAt?.toDate?.()?.getTime?.() ||
    new Date(billing?.accessExpiresAt || 0).getTime?.() ||
    0;

  return (
    billing?.status === "active" &&
    billing?.assinaturaAtiva === true &&
    billing?.paymentApproved === true &&
    billing?.plano === "plus" &&
    accessExpiresAt > agora
  );
}

function formatarData(data) {
  if (!data) return "25/06/2026";

  const valor = data?.toDate?.() || new Date(data);

  if (Number.isNaN(valor.getTime())) return "25/06/2026";

  return valor.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function calcularDiasRestantes(data) {
  const valor = data?.toDate?.() || new Date(data || "2026-06-25T12:00:00");

  if (Number.isNaN(valor.getTime())) return 23;

  const diff = valor.getTime() - Date.now();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

function gerarMesAtual() {
  const data = new Date();
  return `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, "0")}`;
}

function formatarMoeda(valor) {
  return Number(valor || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    maximumFractionDigits: 0,
  });
}

function gerarCodigoConvite(uid, billing) {
  const codigoSalvo =
    billing?.codigoIndicacao ||
    billing?.referralCode ||
    billing?.codigoConvite ||
    billing?.conviteCodigo;

  if (codigoSalvo) return String(codigoSalvo).toUpperCase();
  if (uid) return String(uid).replace(/[^a-zA-Z0-9]/g, "").slice(0, 6).toUpperCase();
  return "";
}

function StatusIcon({ ativo }) {
  if (ativo) {
    return (
      <span className="plus-check">
        <Check size={15} />
      </span>
    );
  }

  return (
    <span className="plus-no">
      <X size={15} />
    </span>
  );
}

function MiniBadge({ tipo, ativo = false }) {
  return (
    <span className={`supporter-mini-badge ${tipo} ${ativo ? "ativo" : ""}`}>
      {tipo === "leaf" ? (
        <span className="badge-leaf" />
      ) : tipo === "x" ? (
        <span className="badge-x">X</span>
      ) : (
        <Star size={24} fill="currentColor" />
      )}
    </span>
  );
}

function FounderSeal({ estado, ano, grande = false }) {
  if (estado?.startsWith("misterio")) {
    return (
      <div className={`founder-seal mystery ${estado} ${grande ? "grande" : ""}`}>
        <span>?</span>
      </div>
    );
  }

  if (estado === "bloqueado") {
    return (
      <div className={`founder-seal locked ${grande ? "grande" : ""}`}>
        <span>X</span>
      </div>
    );
  }

  return (
    <div className={`founder-seal active ${grande ? "grande" : ""}`}>
      <span>X</span>
      {ano && <small>{ano}</small>}
    </div>
  );
}

function PlusMembro({ billing, usuario, periodo }) {
  const renovacao = billing?.accessExpiresAt;
  const dataRenovacao = formatarData(renovacao);
  const diasRestantes = calcularDiasRestantes(renovacao);
  const authUser = getAuth().currentUser;
  const uid = usuario?.uid || billing?.uid || authUser?.uid || "";
  const codigoConvite = gerarCodigoConvite(uid, billing);
  const linkConvite = codigoConvite
    ? `https://invexafinance.com.br/convite/${codigoConvite}`
    : "Seu link será gerado após sincronizar a conta";
  const indicacoesValidas = Number(billing?.indicacoesValidas || billing?.referralsValidos || 0);
  const [conviteCopiado, setConviteCopiado] = useState(false);
  const [impacto, setImpacto] = useState({
    valorAcompanhado: 0,
    relatoriosGerados: Number(billing?.relatoriosGerados || billing?.exportacoesGeradas || 0),
    metasConcluidas: 0,
    economiaEstimada: 0,
    carregando: true,
  });
  const apoiadoresReais = Array.isArray(billing?.apoiadoresIniciais)
    ? billing.apoiadoresIniciais.filter((item) => item?.nome)
    : apoiadoresIniciais;
  const proximaMeta = indicacoesValidas >= 10 ? 25 : 10;
  const progresso = Math.min(100, Math.round((indicacoesValidas / proximaMeta) * 100));

  useEffect(() => {
    if (!uid) {
      setImpacto((atual) => ({ ...atual, carregando: false }));
      return undefined;
    }

    let cancelado = false;
    const mes = periodo || gerarMesAtual();

    async function carregarImpacto() {
      try {
        const [resumo, transacoes, investimentos] = await Promise.all([
          getResumoPeriodo(uid, mes),
          getUltimasTransacoes(uid, mes),
          getInvestimentosPeriodo(uid).catch(() => ({ total: 0 })),
        ]);

        if (cancelado) return;

        const valorTransacoes = transacoes.reduce(
          (total, item) => total + Math.abs(Number(item.valor || 0)),
          0
        );

        setImpacto((atual) => ({
          ...atual,
          valorAcompanhado: valorTransacoes + Number(investimentos?.total || 0),
          relatoriosGerados: Number(billing?.relatoriosGerados || billing?.exportacoesGeradas || 0),
          economiaEstimada: Math.max(0, Number(resumo?.saldo || 0)),
          carregando: false,
        }));
      } catch (error) {
        console.error("Erro ao carregar impacto Plus:", error);
        if (!cancelado) {
          setImpacto((atual) => ({ ...atual, carregando: false }));
        }
      }
    }

    carregarImpacto();

    const unsubscribe = ouvirCofrinhos(uid, (cofrinhos) => {
      const concluidas = cofrinhos.filter((item) => {
        const meta = Number(item.meta || 0);
        const valorAtual = Number(item.valorAtual || 0);
        return meta > 0 && valorAtual >= meta;
      }).length;

      setImpacto((atual) => ({
        ...atual,
        metasConcluidas: concluidas,
      }));
    });

    return () => {
      cancelado = true;
      if (typeof unsubscribe === "function") unsubscribe();
    };
  }, [uid, periodo, billing?.relatoriosGerados, billing?.exportacoesGeradas]);

  async function copiarLinkConvite() {
    if (!codigoConvite) return;

    try {
      await navigator.clipboard.writeText(linkConvite);
      setConviteCopiado(true);
      setTimeout(() => setConviteCopiado(false), 1800);
    } catch (error) {
      console.error("Erro ao copiar link de convite:", error);
    }
  }

  const classeAtual = useMemo(() => {
    const lista = [...classesApoiador].reverse();
    return lista.find((item) => indicacoesValidas >= item.meta) || classesApoiador[0];
  }, [indicacoesValidas]);

  return (
    <section className="plus-page plus-member-page">
      <div className="plus-member-topbar">
        <div>
          <div className="plus-member-title">
            <Crown size={30} />
            <h2>Plus</h2>
          </div>
          <p>Sua área exclusiva do Invexa Plus.</p>
        </div>

        <div className="plus-support-banner">
          <Crown size={22} />
          <span>
            Obrigado por apoiar o <strong>Invexa Finance!</strong>
          </span>
        </div>
      </div>

      <section className="member-hero-card">
        <div>
          <span className="member-pill">Plano ativo</span>
          <h3>
            Invexa Plus <Crown size={28} />
          </h3>
          <p>Você está aproveitando todos os recursos do plano completo.</p>
        </div>

        <div className="member-hero-info">
          <div>
            <CalendarDays size={28} />
            <span>Renovação em</span>
            <strong>{dataRenovacao}</strong>
            <small>Faltam {diasRestantes} dias</small>
          </div>

          <div>
            <ShieldCheck size={28} />
            <span>Status</span>
            <strong>Ativo</strong>
            <small>Renovação automática</small>
          </div>
        </div>
      </section>

      <div className="plus-member-grid two">
        <section className="member-card impact-card">
          <h3>Seu impacto com o Invexa</h3>

          <div className="impact-stats">
            <div>
              <span className="impact-icon green">
                <TrendingUp size={27} />
              </span>
              <strong>{impacto.carregando ? "..." : formatarMoeda(impacto.valorAcompanhado)}</strong>
              <p>valor acompanhado este mês</p>
            </div>
            <div>
              <span className="impact-icon blue">
                <FileText size={27} />
              </span>
              <strong>{impacto.relatoriosGerados}</strong>
              <p>relatórios/exportações gerados</p>
            </div>
            <div>
              <span className="impact-icon purple">
                <Target size={27} />
              </span>
              <strong>{impacto.metasConcluidas}</strong>
              <p>metas concluídas</p>
            </div>
            <div>
              <span className="impact-icon gold">
                <TrendingUp size={27} />
              </span>
              <strong>{impacto.carregando ? "..." : formatarMoeda(impacto.economiaEstimada)}</strong>
              <p>saldo positivo este mês</p>
            </div>
          </div>

          <div className="member-mini-message">
            <TrendingUp size={21} />
            <div>
              <strong>Você está no controle! Continue assim.</strong>
              <span>Evite gastos desnecessários e alcance seus objetivos.</span>
            </div>
            <ArrowRight size={18} />
          </div>
        </section>

        <section className="member-card features-card">
          <h3>Recursos Plus liberados</h3>
          <p>Você tem acesso a todos os recursos premium.</p>

          <div className="features-list">
            {[
              "Relatórios avançados",
              "Exportação de dados",
              "Metas e objetivos ilimitados",
              "Controle de dívidas avançado",
              "Acompanhamento de investimentos",
              "Backup em nuvem",
              "Suporte prioritário",
              "Atualizações exclusivas",
            ].map((item) => (
              <span key={item}>
                <Check size={14} />
                {item}
              </span>
            ))}
          </div>

          <button className="member-outline-button" type="button">
            Ver todos os benefícios <ArrowRight size={17} />
          </button>
        </section>
      </div>

      <section className="member-card supporter-card">
        <div className="supporter-current">
          <div className="supporter-big-seal">
            <FounderSeal estado="ativo" />
          </div>

          <div>
            <div className="supporter-heading">
              <h3>Sua classe de apoiador</h3>
              <span>Novo</span>
            </div>
            <strong>{classeAtual.nome === "founder" ? "FOUNDER" : "Gold Supporter"}</strong>
            <small>{classeAtual.nome === "founder" ? "2026" : "Apoiador nível 4"}</small>
            <p>Você faz parte dos apoiadores que ajudam o Invexa a crescer e transformar vidas.</p>
          </div>
        </div>

        <div className="supporter-progress-area">
          <div className="progress-head">
            <span>{indicacoesValidas} indicações válidas</span>
            <small>Faltam {Math.max(0, proximaMeta - indicacoesValidas)} para a próxima classe</small>
            <strong>{indicacoesValidas} / {proximaMeta}</strong>
          </div>
          <div className="supporter-progress-bar">
            <span style={{ width: `${progresso}%` }} />
          </div>

          <div className="supporter-steps">
            {classesApoiador.map((item, index) => (
              <div className="supporter-step" key={item.id}>
                <MiniBadge tipo={item.tipo} ativo={indicacoesValidas >= item.meta} />
                {index < classesApoiador.length - 1 && <span className="step-line" />}
                <strong>{item.nome}</strong>
                <small>{item.meta} {item.meta === 1 ? "indicação" : "indicações"}</small>
              </div>
            ))}
          </div>
        </div>

        <div className="supporter-rewards">
          <span>Benefícios da sua classe</span>
          <div>
            <p><Zap size={16} /> Acesso antecipado</p>
            <p><Star size={16} /> Badge exclusivo</p>
            <p><Star size={16} /> 7 dias de Plus a cada upgrade</p>
            <p><Users size={16} /> Nome em apoiadores iniciais</p>
          </div>
        </div>
      </section>

      <section className="member-card historical-card">
        <div className="member-section-head">
          <div>
            <h3>Seus selos históricos</h3>
            <p>Conquistas que ficam para sempre.</p>
          </div>
          <button type="button">Entenda os selos <Info size={16} /></button>
        </div>

        <div className="history-timeline">
          {selosHistoricos.map((selo, index) => (
            <article className={`history-seal-card ${selo.estado}`} key={selo.ano}>
              {index < selosHistoricos.length - 1 && <span className="history-line" />}
              <strong>{selo.ano}</strong>
              <FounderSeal estado={selo.estado} grande ano={selo.ano} />
              <h4>{selo.titulo}</h4>
              <span className="history-status">
                {selo.estado === "bloqueado" && <Lock size={13} />}
                {selo.status}
              </span>
              <p>{selo.descricao}</p>
            </article>
          ))}
        </div>

        <div className="history-note">
          <Info size={16} />
          Os selos Founder são anuais, exclusivos e não podem ser adquiridos após o encerramento de cada ciclo.
        </div>
      </section>

      <section className="member-card supporters-wall">
        <div className="member-section-head">
          <div>
            <h3><Trophy size={20} /> Apoiadores Iniciais</h3>
            <p>Pessoas que acreditaram no Invexa desde o começo.</p>
          </div>
          <button type="button">Ver mural histórico <ArrowRight size={16} /></button>
        </div>

        {apoiadoresReais.length > 0 ? (
          <div className="supporters-list">
            {apoiadoresReais.map((item) => (
              <article className="supporter-person" key={`${item.nome}-${item.selo || "founder"}`}>
                <div className="supporter-avatar">
                  {item.iniciais || item.nome.charAt(0).toUpperCase()}
                </div>
                <strong>{item.nome}</strong>
                <span>{item.selo || "FOUNDER 2026"}</span>
                <FounderSeal estado="ativo" />
              </article>
            ))}
          </div>
        ) : (
          <div className="supporters-empty">
            <Users size={32} />
            <strong>Os primeiros apoiadores aparecerão aqui em breve.</strong>
            <span>Assim que existirem Founders reais, eles serão exibidos neste mural histórico.</span>
          </div>
        )}
      </section>

      <div className="plus-member-grid two bottom">
        <section className="member-card invite-card">
          <div className="member-section-head compact">
            <div>
              <h3>Convide amigos e ganhe benefícios</h3>
              <p>Convide amigos para o Invexa e ganhe dias extras no Plus de forma limitada.</p>
            </div>
            <span className="benefit-chip">Benefícios reais</span>
          </div>

          <div className="invite-benefits">
            <div>
              <Users size={25} />
              <span>Você ganha</span>
              <strong>7 dias de Plus</strong>
              <small>para cada amigo que assinar</small>
            </div>
            <div>
              <Gift size={25} />
              <span>Seu amigo ganha</span>
              <strong>7 dias de Plus</strong>
              <small>ao assinar pelo seu convite</small>
            </div>
          </div>

          <div className="invite-link">
            <span>Seu link de convite</span>
            <strong>{linkConvite}</strong>
            <button
              type="button"
              onClick={copiarLinkConvite}
              disabled={!codigoConvite}
              title={conviteCopiado ? "Link copiado" : "Copiar link"}
            >
              {conviteCopiado ? <CheckCircle2 size={17} /> : <Check size={17} />}
            </button>
          </div>

          <button className="member-outline-button" type="button">
            Ver minhas indicações <ArrowRight size={17} />
          </button>
        </section>

        <section className="member-card roadmap-card">
          <h3>Próximas atualizações</h3>
          <p>Estamos sempre evoluindo para você ter a melhor experiência.</p>

          <div className="roadmap-list">
            {proximasAtualizacoes.map((item) => (
              <article key={item.titulo}>
                <span className={`roadmap-status ${item.cor}`}>{item.status}</span>
                <div>
                  <strong>{item.titulo}</strong>
                  <p>{item.descricao}</p>
                </div>
                <ArrowRight size={17} />
              </article>
            ))}
          </div>

          <button className="roadmap-link" type="button">
            Ver roadmap completo <ArrowRight size={17} />
          </button>
        </section>
      </div>

      <section className="member-footer-cta">
        <Sparkles size={28} />
        <div>
          <strong>Aproveite todos os recursos do Invexa Plus.</strong>
          <span>Mais controle, mais organização e mais conquistas para você.</span>
        </div>
        <button type="button">Explorar recursos <ArrowRight size={17} /></button>
      </section>
    </section>
  );
}

function PlusVenda({ ativo, carregando, erro, assinarPlus }) {
  return (
    <section className="plus-page">
      <section className="plus-sales-hero">
        <div className="plus-hero-content">
          <div className="plus-kicker">
            <Crown size={15} />
            Invexa Plus
          </div>

          <h2>Controle financeiro completo para parar de se perder no mês.</h2>

          <p>
            Desbloqueie metas, dívidas, relatórios, exportações e recursos
            avançados para entender melhor seu dinheiro.
          </p>

          <div className="plus-hero-actions">
            <button
              className="plus-main-cta"
              type="button"
              onClick={assinarPlus}
              disabled={ativo || carregando}
            >
              {carregando ? (
                <Loader2 className="spin" size={19} />
              ) : ativo ? (
                <CheckCircle2 size={19} />
              ) : (
                <Sparkles size={19} />
              )}

              {ativo
                ? "Plano ativo"
                : carregando
                ? "Abrindo checkout..."
                : "Desbloquear Invexa Plus"}

              {!ativo && !carregando && <ArrowRight size={18} />}
            </button>

            <div className="plus-price-mini">
              <strong>R$ 14,90/mês</strong>
              <span>Sem anúncios • cancele quando quiser</span>
            </div>
          </div>

          {erro && <div className="plus-error">{erro}</div>}

          <div className="plus-trust-row">
            <span>
              <CheckCircle2 size={15} />
              Pagamento seguro Mercado Pago
            </span>
            <span>
              <CheckCircle2 size={15} />
              Liberação automática
            </span>
            <span>
              <CheckCircle2 size={15} />
              Visual premium
            </span>
          </div>
        </div>

        <aside className="plus-price-card">
          <div className="plus-price-badge">Mais completo</div>

          <div className="plus-plan-icon">
            <Crown size={30} />
          </div>

          <span>INVEXA PLUS</span>

          <strong>R$ 14,90</strong>

          <p>por mês</p>

          <button
            className="plus-card-cta"
            type="button"
            onClick={assinarPlus}
            disabled={ativo || carregando}
          >
            {ativo ? "Plano ativo" : "Assinar agora"}
          </button>

          <ul>
            <li>
              <Check size={16} />
              Metas e cofrinhos
            </li>
            <li>
              <Check size={16} />
              Controle de dívidas
            </li>
            <li>
              <Check size={16} />
              Relatórios e exportações
            </li>
          </ul>
        </aside>
      </section>

      <section className="plus-section-title">
        <span>Recursos premium</span>
        <h3>O que você desbloqueia</h3>
        <p>
          Recursos pensados para quem quer sair do básico e ter mais clareza
          sobre o dinheiro.
        </p>
      </section>

      <div className="plus-benefits-grid">
        {beneficios.map((item) => {
          const Icon = item.icon;

          return (
            <article
              className={`plus-benefit-card ${item.cor}`}
              key={item.titulo}
            >
              <div className="plus-benefit-icon">
                <Icon size={22} />
              </div>

              <div>
                <strong>{item.titulo}</strong>
                <p>{item.descricao}</p>
              </div>
            </article>
          );
        })}
      </div>

      <section className="plus-compare-card">
        <div className="plus-compare-head">
          <div>
            <span>Comparativo</span>
            <h3>Free vs Plus</h3>
          </div>

          <div className="plus-lock-chip">
            <LockKeyhole size={15} />
            Premium
          </div>
        </div>

        <div className="plus-compare-table">
          <div className="plus-compare-row plus-compare-header">
            <span>Recurso</span>
            <strong>Free</strong>
            <strong>Plus</strong>
          </div>

          {comparativo.map((item) => (
            <div className="plus-compare-row" key={item.recurso}>
              <span>{item.recurso}</span>
              <strong>
                <StatusIcon ativo={item.free} />
              </strong>
              <strong>
                <StatusIcon ativo={item.plus} />
              </strong>
            </div>
          ))}
        </div>
      </section>

      <section className="plus-final-cta">
        <div>
          <span>Pronto para evoluir?</span>
          <h3>Desbloqueie o Invexa Plus por R$ 14,90/mês.</h3>
          <p>
            Menos confusão, mais controle e uma visão completa da sua vida
            financeira.
          </p>
        </div>

        <button
          className="plus-main-cta"
          type="button"
          onClick={assinarPlus}
          disabled={ativo || carregando}
        >
          {carregando ? (
            <Loader2 className="spin" size={19} />
          ) : ativo ? (
            <CheckCircle2 size={19} />
          ) : (
            <Crown size={19} />
          )}

          {ativo
            ? "Plano ativo"
            : carregando
            ? "Abrindo checkout..."
            : "Desbloquear Plus"}
        </button>
      </section>
    </section>
  );
}

export default function Plus({ billing, usuario, periodo }) {
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState("");

  useEffect(() => {
    setCarregando(false);
  }, []);

  const ativo = plusAtivo(billing);

  async function assinarPlus() {
    if (ativo || carregando) return;

    setCarregando(true);
    setErro("");

    try {
      const functions = getFunctions(undefined, "us-central1");
      const criarCheckoutPlus = httpsCallable(functions, "criarCheckoutPlus");
      const resposta = await criarCheckoutPlus();

      const url =
        resposta?.data?.initPoint ||
        resposta?.data?.init_point ||
        resposta?.data?.sandbox_init_point;

      if (!url) {
        throw new Error("Link de pagamento não retornado.");
      }

      window.location.href = url;
    } catch (error) {
      console.error(error);
      setErro(
        "Não foi possível abrir o checkout. Confirme se as Functions foram publicadas."
      );
    } finally {
      setCarregando(false);
    }
  }

  if (ativo) {
    return <PlusMembro billing={billing} usuario={usuario} periodo={periodo} />;
  }

  return (
    <PlusVenda
      ativo={ativo}
      carregando={carregando}
      erro={erro}
      assinarPlus={assinarPlus}
    />
  );
}
